import { usePlaceInfo } from '@shared/core/places/usePlaceInfo';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { useState, useEffect } from 'react';
import { useTranslation } from '@shared/core';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { format } from 'date-fns';

/**
 * The calendar day an instant falls on in the event's timezone, as `yyyy-MM-dd`.
 *
 * `fillPageWithEventInfo` renders the card date in the event's zone (`fillPageWithEventInfo.ts:48`),
 * so the day has to be resolved there too. Resolving it in the *viewer's* zone — which
 * `date.setHours(16, 0, 0, 0)` did — printed the neighbouring day whenever the two zones straddled a
 * boundary. Falls back to the viewer's zone only when the event has no timezone.
 */
export function getEventDay(date: string | number, timezone: string | undefined): string;
export function getEventDay(date: string | number | null | undefined, timezone: string | undefined): string | undefined;
export function getEventDay(date: string | number | null | undefined, timezone: string | undefined): string | undefined {
  if (!date) return undefined;
  return timezone ? formatInTimeZone(new Date(date), timezone, 'yyyy-MM-dd') : format(new Date(date), 'yyyy-MM-dd');
}

/** 4pm on `eventDay` in the event's timezone, as an instant. Exported for the spec. */
export function setFourPm(eventDay: string, timezone: string | undefined): number;
export function setFourPm(eventDay: string | undefined, timezone: string | undefined): number | undefined;
export function setFourPm(eventDay: string | undefined, timezone: string | undefined): number | undefined {
  if (!eventDay) return undefined;
  if (!timezone) return new Date(`${eventDay}T16:00:00`).getTime();
  return zonedTimeToUtc(`${eventDay}T16:00:00`, timezone).getTime();
}

export type PrimaryEventLocation = {
  Venue: string;
  Address: string;
  City: string;
  CityState: string;
  CityST: string;
  utcMilliseconds: number;
  timezone: string | undefined;
};

// Precedence (higher on top):
// VENUE/NAME   LOCATION     DATE       TIME
// VenueInfo    VenueInfo
//              Location     Finalized  4pm      <- Location is not guaranteed
// Placeholder  Placeholder  Today      4pm
export function usePrimaryEventLocation(): { location: PrimaryEventLocation; loading: boolean } {
  const { tq } = useTranslation('stationery');
  const [location, setLocation] = useState<PrimaryEventLocation>({
    Venue: tq`Your Venue`,
    Address: '8 Joy Lane',
    City: 'Seattle',
    CityState: 'Seattle, Washington',
    CityST: 'Seattle, WA',
    utcMilliseconds: setFourPm(getEventDay(Date.now(), undefined), undefined),
    timezone: undefined,
  });
  const { eventInfo, loading: eventLoading } = useEventInfo();
  const { venueInfo, loading: venueLoading } = useEventPlaces();
  const loading = eventLoading || venueLoading;

  const placeId = loading ? null : (venueInfo.googlePlaceId ?? eventInfo?.locationInfo?.placeId);
  const { result: place, isLoading: gettingPlaceInfo, isCalled } = usePlaceInfo('essentials', 'primaryEventLocation', placeId);

  useEffect(() => {
    if (!place) return;

    const addr = (type: string) => place.addressComponents.find(c => c.types.includes(type)) ?? { longText: '', shortText: '' };
    const Venue = venueInfo.name ?? tq`Your Venue`;
    // prettier-ignore
    const [Address, locality] = addr('route').longText
      ? (place.formattedAddress ?? '').split(', ')
      : [ // ex: ['King County', 'Seattle, WA']
        addr('administrative_area_level_2').longText,
        `${addr('locality').longText}, ${addr('administrative_area_level_1').shortText}`
      ];
    const country = addr('country').shortText;
    const City = addr('locality').longText;
    const state = addr('administrative_area_level_1');
    const CityState = country === 'US' ? `${City}, ${state.longText}` : (locality ?? '');
    const CityST = country === 'US' ? `${City}, ${state.shortText}` : (locality ?? '');

    const timezone = eventInfo?.finalizedDate?.timezone;
    // prettier-ignore
    const utcMilliseconds =
      setFourPm(getEventDay(eventInfo?.finalizedDate?.milliseconds, timezone), timezone)
      ?? setFourPm(getEventDay(eventInfo?.finalizedDate?.timestamp, timezone), timezone)
      ?? setFourPm(getEventDay(Date.now(), timezone), timezone);

    setLocation({ Venue, Address, City, CityState, CityST, utcMilliseconds, timezone });
  }, [place, eventInfo, venueInfo, tq]);

  // When no placeId exists (public gallery case), we're done loading once other data is loaded
  const isPlaceInfoLoading = placeId ? gettingPlaceInfo || !isCalled : false;

  return { location, loading: loading || isPlaceInfoLoading };
}
